import { RevxInputNumber } from 'components/RevxInputNumber';
import { MeasureType, METRIC_DISPLAY_DECIMALS } from 'domains/measurementDomain';
import { useMeasureField } from 'hooks/useMeasurementUnit';
import { ComponentProps } from 'react';

type BaseProps = Omit<
  ComponentProps<typeof RevxInputNumber>,
  'value' | 'onChange'
>;

interface Props extends BaseProps {
  /** The value as the API returned it, already in the unit the viewer is working in. */
  value?: number;
  /** Called with the number the user typed, verbatim - the API converts, not this. */
  onChange?: (value: number) => void;
  /** @internal antd injects this via Form.Item; not part of the public surface. */
  /** Which quantity this field holds, so the right unit and conversion are used. */
  type?: MeasureType;
}

/**
 * The `RevxInputNumber` flavour of {@link RevxMeasureInput}, for the SKU size tables.
 *
 * Carries no unit suffix of its own: these fields sit in `W x L` columns whose headers already name
 * the unit once, and repeating it in every cell of a size table is noise.
 *
 * Same contract as the other flavours - values arrive in the viewer's unit and are sent back
 * exactly as typed, with the API deciding which column each side belongs in.
 *
 * Shows three decimals but holds all six. A size typed in millimetres is kept as six decimals of
 * inches, because that is what brings an entered 888mm back as 888 instead of 887.98, and a
 * reader has no use for the tail. Focusing a cell reveals the whole number.
 */
export const RevxMeasureNumberInput = ({
  value,
  onChange,
  type = MeasureType.LENGTH,
  ...rest
}: Props) => {
  const field = useMeasureField(type, {
    value,
    // The form only ever holds a number; an emptied field clears to undefined upstream.
    onChange: (stored) => onChange?.(stored as number),
  });

  return (
    <RevxInputNumber
      displayPrecision={METRIC_DISPLAY_DECIMALS}
      {...rest}
      value={field.value as number | undefined}
      onChange={(next: number) => field.onChange(next)}
    />
  );
};
